// common/hooks/useRelationshipTypes.ts
//
// React Query hook for the relationship vocabulary (GET /types/relationship).
// Static reference data, so cached with a 1h staleTime and shared across the
// family invite/edit and roster link-to-existing pickers (web + mobile). "Self"
// is already filtered out by the service.

import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import {
  listRelationshipTypes,
  type RelationshipType,
} from '../services/types/listRelationshipTypes'

export type { RelationshipType }

// 1 hour — relationship types are static reference data.
const STALE_TIME = 60 * 60 * 1000

export const useRelationshipTypes = (
  // Pass `includeSelf` in the edit-family flow to keep the "Me"/Self entry so a
  // contact can be marked as the player themselves. Cached under a separate
  // query key so it never collides with the default (filtered) list.
  { includeSelf = false }: { includeSelf?: boolean } = {}
): {
  relationshipTypes: RelationshipType[]
  loading: boolean
  error: Error | null
} => {
  const { data, isPending, error } = useQuery({
    queryKey: includeSelf
      ? queryKeys.relationshipTypes.allWithSelf
      : queryKeys.relationshipTypes.all,
    queryFn: () => listRelationshipTypes({ includeSelf }),
    staleTime: STALE_TIME,
  })

  return {
    relationshipTypes: data ?? [],
    loading: isPending,
    error: (error as Error) ?? null,
  }
}
